import {useCallback} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {useWindowEventBlockerRef} from '../../../../../../common/useWindowEventBlockerRef.hook';
import {goToNextPage, goToPrevPage} from '../state/textViewerSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {TextViewerCoreOption} from '../type/TextViewerCoreOption.type';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {useViewerMenuHandler} from './useViewerMenuHandler';

export function useTextViewerPageHandler({
  productId,
  option,
}: {
  productId: number;
  option: TextViewerCoreOption;
}) {
  const {
    currentPage,
    currentPageCount,
    currentChapterId,
    chapterCount,
    readType,
  } = useTypedSelector(state => ({
    currentPage: state.textViewer.viewData[productId]?.currentPage,
    currentPageCount: state.textViewer.viewData[productId]?.currentPageCount,
    currentChapterId: state.textViewer.viewData[productId]?.currentChapterId,
    chapterCount: state.textViewer.viewData[productId]?.chapterCount,
    readType: state.textViewer.settings.readType,
  }));
  const dispatch = useTypedDispatch();
  const {isMobile} = requestContext.requestInfo.display;

  const {shouldUseNonstopWatching, handleMoveNextProduct} =
    useViewerMenuHandler({
      isMobile,
    });

  const windowClickEventBlockerRef = useWindowEventBlockerRef('click');

  const goToPrevPageHandler = useCallback(
    (blockWindowClickEvent = false) => {
      dispatch(setIsViewerMenuExposed({isExposed: false}));
      dispatch(goToPrevPage({productId}));
      if (blockWindowClickEvent) {
        windowClickEventBlockerRef.current = true;
      }
    },
    [dispatch, productId, windowClickEventBlockerRef]
  );

  const goToNextPageHandler = useCallback(
    (blockWindowClickEvent = false) => {
      dispatch(setIsViewerMenuExposed({isExposed: false}));
      if (blockWindowClickEvent) {
        windowClickEventBlockerRef.current = true;
      }
      if (shouldUseNonstopWatching) {
        // 아직 카운트된 페이지가 없어 현재 위치가 작품의 마지막인지 알 수가 없습니다. 아무것도 하지 않습니다.
        if (!currentPageCount || !chapterCount || currentPage === undefined) {
          return;
        }

        const nextPage =
          currentPage +
          (option.isTwoColumnLayout && readType === TextViewerReadType.Page
            ? 2
            : 1);
        if (
          nextPage > currentPageCount &&
          currentChapterId === chapterCount - 1
        ) {
          handleMoveNextProduct();
          return;
        }
      }
      dispatch(goToNextPage({productId}));
    },
    [
      chapterCount,
      currentChapterId,
      currentPage,
      currentPageCount,
      dispatch,
      handleMoveNextProduct,
      option.isTwoColumnLayout,
      productId,
      readType,
      shouldUseNonstopWatching,
      windowClickEventBlockerRef,
    ]
  );

  return {
    goToPrevPageHandler,
    goToNextPageHandler,
  };
}
